import { useEffect, useRef } from "react";

const MIN_SCORE = 0;
const MAX_SCORE = 10;
const TEN_FROM_ONE_WINDOW_MS = 1500;
const TEXT_INPUT_TYPES = ["text", "number", "search", "email", "password", "tel", "url"];

const isTextEntryTarget = (target) => {
  if (!target) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName ? target.tagName.toLowerCase() : "";
  if (tag === "textarea") return true;
  if (tag === "input") {
    const type = (target.getAttribute("type") || "text").toLowerCase();
    return TEXT_INPUT_TYPES.includes(type);
  }
  return false;
};

/**
 * Shared keyboard shortcuts for the LPR scoring tools (AO-33366 Camera Scoring and
 * AO-33653 Plate Search edit drawer). While enabled, a global keydown listener maps:
 * digits 1–9 set that score (1 then 0 within 1.5s = 10, a lone 0 = 0), and Enter/Space
 * saves when saving is allowed. Keys are ignored while a text input is focused.
 *
 * @param {Object} params
 * @param {boolean} params.enabled - whether the listener is active.
 * @param {boolean} params.canSave - whether Enter/Space should trigger a save.
 * @param {(value: number) => void} params.onSetScore - sets a numeric score (0–10).
 * @param {() => void} params.onSave - saves the current score.
 */
const useScoreKeyboardShortcuts = ({ enabled, canSave, onSetScore, onSave }) => {
  const canSaveRef = useRef(canSave);
  canSaveRef.current = canSave;
  const onSetScoreRef = useRef(onSetScore);
  onSetScoreRef.current = onSetScore;
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  useEffect(() => {
    if (!enabled) return undefined;

    let lastOneAt = 0;

    const handleKeyDown = (event) => {
      if (isTextEntryTarget(event.target)) {
        return;
      }

      if (event.key >= "1" && event.key <= "9") {
        event.preventDefault();
        onSetScoreRef.current(Number(event.key));
        lastOneAt = event.key === "1" ? Date.now() : 0;
        return;
      }

      if (event.key === "0") {
        event.preventDefault();
        const isTen = Date.now() - lastOneAt <= TEN_FROM_ONE_WINDOW_MS;
        onSetScoreRef.current(isTen ? MAX_SCORE : MIN_SCORE);
        lastOneAt = 0;
        return;
      }

      if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
        if (canSaveRef.current) {
          event.preventDefault();
          onSaveRef.current();
        }
        lastOneAt = 0;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enabled]);
};

export default useScoreKeyboardShortcuts;
